import { InlineMath, BlockMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";


function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function formatShiftedVar(varName, value) {
  if (value === 0) return `${varName}^2`;
  if (value > 0) return `(${varName} - ${value})^2`;
  return `(${varName} + ${Math.abs(value)})^2`;
}

function formatSubtractedNumber(value) {
  return value < 0 ? `(${value})` : `${value}`;
}

function formatSquaredNumber(value) {
  return value < 0 ? `(${value})^2` : `${value}^2`;
}

export function generateTask() {
  const a = getRandomInt(-5, 5);
  const b = getRandomInt(-5, 5);
  const r = getRandomInt(2, 6);
  const positions = ["inside", "on", "outside"];
  const position = positions[getRandomInt(0, positions.length - 1)];
  const offsets = [];
  const maxDistanceSquared = (r + 2) ** 2;

  for (let dx = -r - 2; dx <= r + 2; dx += 1) {
    for (let dy = -r - 2; dy <= r + 2; dy += 1) {
      const distanceSquared = dx * dx + dy * dy;
      if (
        (position === "inside" && distanceSquared > 0 && distanceSquared < r * r) ||
        (position === "on" && distanceSquared === r * r) ||
        (position === "outside" && distanceSquared > r * r && distanceSquared <= maxDistanceSquared)
      ) {
        offsets.push({ dx, dy });
      }
    }
  }

  const { dx, dy } = offsets[getRandomInt(0, offsets.length - 1)];
  const x = a + dx;
  const y = b + dy;

  return {
    type: "CheckPointCirclePositionTask",
    data: {
      center: { x: a, y: b },
      radius: r,
      point: { x, y },
    },
  };
}
export function generateTasks() {
  return [generateTask()];
}

export function renderTask(task) {
  const data = task?.data ?? task;

  if (!data?.center || !data?.point || !data?.radius) {
    console.error("Invalid or missing task data in renderTask:", task);
    return <div style={{ color: 'red' }}>Brak danych do zadania.</div>;
  }

  const { center, radius, point } = data;

  return (
    <div className="space-y-2">
      <p>Określ położenie punktu względem okręgu.</p>
      <BlockMath math={`${formatShiftedVar("x", center.x)} + ${formatShiftedVar("y", center.y)} = ${radius ** 2}`} />
      <BlockMath math={`P = (${point.x}, ${point.y})`} />
      <p>Wybierz jedną z odpowiedzi:</p>
    </div>
  );
}


const options = [
  { value: "inside", label: "wewnątrz okręgu" },
  { value: "on", label: "na okręgu" },
  { value: "outside", label: "na zewnątrz okręgu" }
];

export function renderInput(_, userInput, setUserInput, disabled) {
  return (
    <SelectInput
      options={options}
      value={userInput}
      onChange={(e) => setUserInput(e?.target?.value ?? e)}
      disabled={disabled}
      placeholder="Wybierz"
    />
  );
}

export function checkAnswer(taskOrData, userInput) {
  const data = taskOrData?.data ?? taskOrData;

  if (!data || !data.center || !data.radius || !data.point) {
    console.error("Invalid or missing task data:", data);
    return {
      correct: false,
      expected: [],
      userInput: userInput ?? null,
    };
  }

  const { center, radius, point } = data;

  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const dist2 = dx * dx + dy * dy;
  const r2 = radius * radius;

  let correctAnswer;
  if (dist2 < r2) correctAnswer = "inside";
  else if (dist2 === r2) correctAnswer = "on";
  else correctAnswer = "outside";

  const isCorrect = userInput === correctAnswer;

  return {
    correct: isCorrect,
    expected: [correctAnswer],
    userInput: userInput ?? null,
  };
}

export function renderAnswer(task) {
  const data = task?.data ?? task;

  if (!data?.center || !data?.point || !data?.radius) {
    console.error("Invalid or missing task data in renderAnswer:", task);
    return <div style={{ color: 'red' }}>Brak danych do odpowiedzi.</div>;
  }

  const { center, radius, point } = data;

  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const dist2 = dx * dx + dy * dy;
  const r2 = radius * radius;

  let position;
  if (dist2 < r2) position = "wewnątrz okręgu";
  else if (dist2 === r2) position = "na okręgu";
  else position = "na zewnątrz okręgu";

  return (
    <div style={{ textAlign: "left", marginTop: "1rem" }}>
      Poprawna odpowiedź: <strong>{position}</strong>
    </div>
  );
}

function resolveData(task) {
  if (!task) return null;
  if (task.data) return task.data;
  if (task.center && task.radius && task.point) return task;
  return null;
}


export function renderExplanation(task) {
  const data = resolveData(task);

  if (!data?.center || !data?.point || !data?.radius) {
    console.error("Invalid task data in renderExplanation:", task);
    return <div style={{ color: 'red' }}>Brak danych do wyjaśnienia.</div>;
  }

  const { center, radius, point } = data;

  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const dist2 = dx * dx + dy * dy;
  const r2 = radius * radius;

  const dist = Math.sqrt(dist2);
  const r = radius;

  const isDSquare = Number.isInteger(dist);

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Porównujemy równanie z postacią <InlineMath math="(x-a)^2+(y-b)^2=r^2" /> i odczytujemy środek oraz promień:
        <BlockMath math={`S=(${center.x}, ${center.y}), \\quad r^2=${r2} \\Rightarrow r=\\sqrt{${r2}}=${r}`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Podstawiamy współrzędne punktu i środka do wzoru na odległość:
        <BlockMath math={
          `d = \\sqrt{(${point.x} - ${formatSubtractedNumber(center.x)})^2 + (${point.y} - ${formatSubtractedNumber(center.y)})^2} = \\sqrt{${formatSquaredNumber(dx)} + ${formatSquaredNumber(dy)}} = \\sqrt{${dist2}} ${isDSquare ? `= ${dist}` : `\\approx ${dist.toFixed(2)}`}`
        } />
      </div>

      <div>
        <strong>Krok 3:</strong> Porównujemy wartości:
        <BlockMath math={`d ${isDSquare ? `= ${dist}` : `\\approx ${dist.toFixed(2)}`} \\quad\\text{oraz}\\quad r = ${r}`} />
        {dist < r && <p>Ponieważ <InlineMath math="d < r" />, punkt leży <strong>wewnątrz</strong> okręgu.</p>}
        {dist === r && <p>Ponieważ <InlineMath math="d = r" />, punkt leży <strong>na</strong> okręgu.</p>}
        {dist > r && <p>Ponieważ <InlineMath math="d > r" />, punkt leży <strong>na zewnątrz</strong> okręgu.</p>}
      </div>
    </div>
  );
}


export function renderUserAnswer(userInput) {
  if (userInput === "inside") return "wewnątrz okręgu";
  if (userInput === "on") return "na okręgu";
  if (userInput === "outside") return "na zewnątrz okręgu";
  return "";
}
